import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  ImageIcon,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';
import type { Language } from '@/types';
import {
  deleteQCReport,
  fetchQCReports,
  fetchSiteVisits,
  type QCReportRow,
  type QCVisitOption,
} from '../api';
import { QCPhotoGrid } from './QCPhotoGrid';
import { QCReportFormDialog } from './QCReportFormDialog';

interface QCSiteViewProps {
  siteId: string;
  language: Language;
  onBack: () => void;
}

type SiteDetail = {
  id: string;
  name: string;
  address: string;
  customer_number: string | null;
};

const OK_INK = '#1B7F4D';
const FAIL_INK = '#C24138';

const copy = {
  en: {
    back: 'BRF:er',
    conductQc: 'CONDUCT QC',
    noRecent: 'No visits in the last 30 days',
    previousVisits: 'SENASTE STÄDNINGAR — 30D',
    previousQcs: 'TIDIGARE QC',
    noQcs: 'No QCs yet.',
    info: 'INFO',
    totalThisYear: 'QC THIS YEAR',
    avgThisYear: 'AVG SCORE THIS YEAR',
    lastQcDate: 'LAST QC',
    blocks: 'blocks',
    extrasOne: 'extra',
    extrasMany: 'extras',
    photos: 'photos',
    photosOne: 'photo',
    pass: 'GODKÄND',
    fail: 'UNDERKÄND',
    inspectorBy: 'by',
    delete: 'Delete',
    confirmDelete: 'Delete this QC report?',
    loading: 'Loading…',
    none: '—',
    unassigned: 'Unassigned',
    cleaner: 'Cleaner',
    blocksRated: 'blocks rated',
    avgVisit: 'Avg',
    noNotes: 'No notes',
    close: 'Close',
    viewQc: 'QC details',
    failedBanner: 'UNDERKÄND KVALITETSKONTROLL',
    failedBannerSub: 'Most recent inspection failed (last 30 days)',
    status: {
      completed: 'Completed',
      approved: 'Approved',
      assigned: 'Assigned',
      planned: 'Planned',
      cancelled: 'Cancelled',
    },
  },
  sv: {
    back: 'BRF:er',
    conductQc: 'GENOMFÖR QC',
    noRecent: 'Inga besök de senaste 30 dagarna',
    previousVisits: 'SENASTE STÄDNINGAR — 30D',
    previousQcs: 'TIDIGARE QC',
    noQcs: 'Inga QC än.',
    info: 'INFO',
    totalThisYear: 'ANTAL QC I ÅR',
    avgThisYear: 'SNITTPOÄNG I ÅR',
    lastQcDate: 'SENASTE QC',
    blocks: 'moment',
    extrasOne: 'extra',
    extrasMany: 'extra',
    photos: 'bilder',
    photosOne: 'bild',
    pass: 'GODKÄND',
    fail: 'UNDERKÄND',
    inspectorBy: 'av',
    delete: 'Ta bort',
    confirmDelete: 'Ta bort denna QC-rapport?',
    loading: 'Laddar…',
    none: '—',
    unassigned: 'Ej tilldelad',
    cleaner: 'Städare',
    blocksRated: 'moment betygsatta',
    avgVisit: 'Snitt',
    noNotes: 'Inga anteckningar',
    close: 'Stäng',
    viewQc: 'QC-detaljer',
    failedBanner: 'UNDERKÄND KVALITETSKONTROLL',
    failedBannerSub: 'Senaste kontrollen underkändes (senaste 30 dagarna)',
    status: {
      completed: 'Klar',
      approved: 'Godkänd',
      assigned: 'Tilldelad',
      planned: 'Planerad',
      cancelled: 'Avbruten',
    },
  },
} as const;

function ScoreNumeral({ score, size = 'sm' }: { score: number; size?: 'sm' | 'lg' }) {
  const ink = score >= 3 ? OK_INK : FAIL_INK;
  const cls = size === 'lg' ? 'text-xl' : 'text-xs';
  return (
    <span className={`font-mono tabular-nums ${cls}`} style={{ color: ink }}>
      {Number.isInteger(score) ? score : score.toFixed(2)}
    </span>
  );
}

function PassChip({ passed, label }: { passed: boolean; label: string }) {
  const ink = passed ? OK_INK : FAIL_INK;
  const bg = passed ? 'rgba(27,127,77,0.10)' : 'rgba(194,65,56,0.12)';
  return (
    <span className="cmd-label px-1.5 py-0.5 rounded text-[8px]" style={{ background: bg, color: ink }}>
      {label}
    </span>
  );
}

type SiteViewCopy = typeof copy['en'] | typeof copy['sv'];

function statusLabel(status: string, text: SiteViewCopy): string {
  return (text.status as Record<string, string>)[status] ?? status;
}

function describeBlocks(visit: QCVisitOption, text: SiteViewCopy): string {
  const recurringCount = Math.max(0, visit.block_count - visit.extras_count);
  const parts: string[] = [];
  parts.push(`${visit.block_count} ${text.blocks}`);
  const preview = visit.block_labels.slice(0, 2);
  if (preview.length > 0) {
    parts.push(preview.join(', '));
  }
  if (visit.extras_count > 0) {
    const extrasLabel = visit.extras_count === 1 ? text.extrasOne : text.extrasMany;
    parts.push(`+${visit.extras_count} ${extrasLabel}`);
  }
  // Suppress unused warning
  void recurringCount;
  return parts.join(' · ');
}

export function QCSiteView({ siteId, language, onBack }: QCSiteViewProps) {
  const text = copy[language];
  const { session, role } = useAuth();
  const token = session?.access_token ?? null;
  const isAdmin = role === 'admin';

  const [site, setSite] = useState<SiteDetail | null>(null);
  const [visits, setVisits] = useState<QCVisitOption[]>([]);
  const [reports, setReports] = useState<QCReportRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [openReportJobId, setOpenReportJobId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const loadAll = async () => {
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const [siteRes, nextVisits, nextReports] = await Promise.all([
        supabase
          .from('sites')
          .select('id, name, address, customer_number')
          .eq('id', siteId)
          .maybeSingle(),
        fetchSiteVisits(token, siteId, 30),
        fetchQCReports(token, { siteId }),
      ]);

      if (siteRes.error) throw siteRes.error;
      setSite(siteRes.data
        ? {
            id: siteRes.data.id,
            name: siteRes.data.name,
            address: siteRes.data.address ?? '',
            customer_number: siteRes.data.customer_number ?? null,
          }
        : null);
      setVisits(nextVisits);
      setReports(nextReports);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, token]);

  const handleDelete = async (id: string) => {
    if (!token) return;
    if (!window.confirm(text.confirmDelete)) return;
    setDeletingId(id);
    try {
      await deleteQCReport(token, id);
      setReports((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const groupedReports = useMemo(() => {
    type Group = {
      job_id: string;
      job_scheduled_for: string;
      cleaner_name: string | null;
      inspector_name: string | null;
      latest_inspected_at: string;
      avg: number;
      passed: boolean;
      reports: QCReportRow[];
    };
    const byJob = new Map<string, QCReportRow[]>();
    for (const r of reports) {
      const list = byJob.get(r.job_id) ?? [];
      list.push(r);
      byJob.set(r.job_id, list);
    }
    const groups: Group[] = [];
    for (const [jobId, list] of byJob) {
      const sorted = [...list].sort((a, b) =>
        b.inspected_at.localeCompare(a.inspected_at),
      );
      const avg = sorted.reduce((sum, r) => sum + r.score, 0) / sorted.length;
      groups.push({
        job_id: jobId,
        job_scheduled_for: sorted[0].job_scheduled_for,
        cleaner_name: sorted[0].cleaner_name,
        inspector_name: sorted[0].inspector_name,
        latest_inspected_at: sorted[0].inspected_at,
        avg,
        passed: avg >= 3,
        reports: sorted,
      });
    }
    groups.sort((a, b) =>
      b.latest_inspected_at.localeCompare(a.latest_inspected_at),
    );
    return groups;
  }, [reports]);

  const stats = useMemo(() => {
    const year = new Date().getFullYear();
    const yearReports = reports.filter((r) => r.inspected_at.startsWith(String(year)));
    const avg =
      yearReports.length === 0
        ? null
        : yearReports.reduce((sum, r) => sum + r.score, 0) / yearReports.length;
    const last = reports.length > 0 ? reports[0] : null;
    return {
      yearCount: yearReports.length,
      yearAvg: avg,
      lastInspectedAt: last?.inspected_at ?? null,
    };
  }, [reports]);

  // Deep-link arrival from an avvikelse chip (#/qc/{siteId}) lands here; make a
  // failed inspection in the last 30d unmissable. Mirrors the qc_failed_30d flag.
  const failedRecently = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    const cutoffIso = cutoff.toISOString().slice(0, 10);
    return reports.some((r) => !r.passed && r.inspected_at.slice(0, 10) >= cutoffIso);
  }, [reports]);

  const canConductQc = visits.length > 0;

  return (
    <div className="space-y-4 pb-6">
      <button
        type="button"
        onClick={onBack}
        className="cmd-label inline-flex items-center gap-1 text-[#607C6E] transition-colors hover:text-[#8A6A47]"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        {text.back}
      </button>

      {/* Header panel */}
      <div className="cmd-panel cmd-panel-inner rounded p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <span className="cmd-label">KVALITETSKONTROLL</span>
            <h1 className="font-mono text-lg text-[#073331] leading-tight">
              {site?.name ?? (isLoading ? text.loading : '—')}
            </h1>
            {site?.address && (
              <p className="font-mono text-[10px] text-[#607C6E] leading-tight">
                {site.address}
                {site.customer_number ? ` · #${site.customer_number}` : ''}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setIsDialogOpen(true)}
            disabled={!canConductQc || isLoading}
            className="cmd-label inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-[#8A6A47] transition-colors hover:bg-[#073331]/[0.04] hover:text-[#073331] disabled:opacity-40 disabled:hover:bg-transparent"
            style={{ border: '1px solid rgba(7,51,49,0.12)' }}
          >
            <Plus className="h-3 w-3" />
            {text.conductQc}
          </button>
        </div>

        {/* Failed-QC arrival banner */}
        {failedRecently && (
          <div
            className="mt-3 flex items-center gap-2 rounded px-3 py-2"
            style={{ border: '1px solid rgba(194,65,56,0.35)', background: 'rgba(194,65,56,0.06)' }}
          >
            <span className="cmd-label" style={{ color: FAIL_INK }}>{text.failedBanner}</span>
            <span className="font-mono text-[10px] text-[#607C6E]">{text.failedBannerSub}</span>
          </div>
        )}
      </div>

      {error && (
        <div
          className="rounded px-3 py-2 font-mono text-xs"
          style={{ border: '1px solid rgba(194,65,56,0.35)', background: 'rgba(194,65,56,0.06)', color: FAIL_INK }}
        >
          {error}
        </div>
      )}

      {/* Section 1: previous cleanings */}
      <div className="cmd-panel cmd-panel-inner rounded p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="cmd-label">{text.previousVisits}</span>
          {!isLoading && (
            <span className="cmd-label text-[#8A6A47] font-mono tabular-nums">{visits.length}</span>
          )}
        </div>
        {isLoading ? (
          <div className="flex items-center gap-2 py-4 font-mono text-xs text-[#607C6E]">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {text.loading}
          </div>
        ) : visits.length === 0 ? (
          <div className="py-4 font-mono text-xs text-[#607C6E]">{text.noRecent}</div>
        ) : (
          <ul className="divide-y divide-[#073331]/10">
            {visits.map((visit) => {
              const expanded = expandedJobId === visit.job_id;
              const photoLabel = visit.photo_count === 1 ? text.photosOne : text.photos;
              return (
                <li key={visit.job_id}>
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedJobId(expanded ? null : visit.job_id)
                    }
                    className="group flex w-full items-center gap-3 py-2 text-left transition-colors hover:bg-[#073331]/[0.04]"
                  >
                    {expanded ? (
                      <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[#607C6E]" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[#607C6E] group-hover:text-[#8A6A47] transition-colors" />
                    )}
                    <div className="flex-1 space-y-0.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono tabular-nums text-xs text-[#073331]">{visit.scheduled_for}</span>
                        <span className="font-mono text-xs text-[#2E4640] group-hover:text-[#073331] transition-colors">
                          {visit.cleaner_name ?? text.unassigned}
                        </span>
                        <span className="cmd-label text-[#607C6E]">
                          {statusLabel(visit.status, text)}
                        </span>
                      </div>
                      <div className="font-mono text-[10px] text-[#607C6E]">
                        {describeBlocks(visit, text)}
                      </div>
                    </div>
                    <span className="cmd-label inline-flex shrink-0 items-center gap-1 text-[#607C6E]">
                      <ImageIcon className="h-3 w-3" />
                      {visit.photo_count} {photoLabel}
                    </span>
                  </button>
                  {expanded && (
                    <div className="pb-3 pl-6">
                      <QCPhotoGrid
                        jobId={visit.job_id}
                        token={token}
                        language={language}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Section 2: previous QCs */}
      <div className="cmd-panel cmd-panel-inner rounded p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="cmd-label">{text.previousQcs}</span>
          {!isLoading && (
            <span className="cmd-label text-[#8A6A47] font-mono tabular-nums">{groupedReports.length}</span>
          )}
        </div>
        {isLoading ? (
          <div className="flex items-center gap-2 py-4 font-mono text-xs text-[#607C6E]">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {text.loading}
          </div>
        ) : groupedReports.length === 0 ? (
          <div className="py-4 font-mono text-xs text-[#607C6E]">{text.noQcs}</div>
        ) : (
          <ul className="divide-y divide-[#073331]/10">
            {groupedReports.map((group) => (
              <li key={group.job_id}>
                <button
                  type="button"
                  onClick={() => setOpenReportJobId(group.job_id)}
                  className="group flex w-full items-center gap-3 py-2 text-left transition-colors hover:bg-[#073331]/[0.04]"
                >
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[#607C6E] group-hover:text-[#8A6A47] transition-colors" />
                  <div className="flex-1 space-y-0.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono tabular-nums text-xs text-[#073331]">
                        {group.job_scheduled_for || group.latest_inspected_at.slice(0, 10)}
                      </span>
                      <span className="font-mono text-xs text-[#2E4640] group-hover:text-[#073331] transition-colors">
                        {group.cleaner_name ?? text.unassigned}
                      </span>
                      <span className="font-mono text-[10px] text-[#607C6E]">
                        {text.avgVisit} <ScoreNumeral score={group.avg} />
                      </span>
                      <PassChip passed={group.passed} label={group.passed ? text.pass : text.fail} />
                    </div>
                    <div className="font-mono text-[10px] text-[#607C6E]">
                      {group.reports.length} {text.blocksRated}
                      {group.inspector_name ? ` · ${text.inspectorBy} ${group.inspector_name}` : ''}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Section 3: info */}
      <div className="cmd-panel cmd-panel-inner rounded p-4">
        <span className="cmd-label">{text.info}</span>
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <div className="cmd-label">{text.totalThisYear}</div>
            <div className="mt-0.5 font-mono tabular-nums text-xl text-[#073331]">{stats.yearCount}</div>
          </div>
          <div>
            <div className="cmd-label">{text.avgThisYear}</div>
            <div className="mt-0.5">
              {stats.yearAvg == null ? (
                <span className="font-mono tabular-nums text-xl text-[#607C6E]">{text.none}</span>
              ) : (
                <ScoreNumeral score={stats.yearAvg} size="lg" />
              )}
            </div>
          </div>
          <div>
            <div className="cmd-label">{text.lastQcDate}</div>
            <div className="mt-0.5 font-mono tabular-nums text-xl text-[#073331]">
              {stats.lastInspectedAt ? stats.lastInspectedAt.slice(0, 10) : text.none}
            </div>
          </div>
        </div>
      </div>

      {site && (
        <QCReportFormDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          siteId={site.id}
          siteName={site.name}
          token={token}
          language={language}
          onCreated={() => void loadAll()}
        />
      )}

      {(() => {
        const openGroup = groupedReports.find((g) => g.job_id === openReportJobId);
        return (
          <Dialog
            open={Boolean(openGroup)}
            onOpenChange={(next) => !next && setOpenReportJobId(null)}
          >
            <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col overflow-hidden border-[#073331]/12 bg-white p-0">
              <DialogHeader className="px-6 pt-6">
                <DialogTitle className="cmd-label text-[#073331] text-[11px]">{text.viewQc}</DialogTitle>
                <DialogDescription className="font-mono text-[10px] text-[#607C6E]">
                  {openGroup
                    ? `${openGroup.job_scheduled_for || openGroup.latest_inspected_at.slice(0, 10)} · ${openGroup.cleaner_name ?? text.unassigned} · ${text.avgVisit} ${openGroup.avg.toFixed(2)}`
                    : ''}
                </DialogDescription>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto cmd-scroll px-6 py-4">
                {openGroup ? (
                  <div className="space-y-3">
                    {openGroup.reports.map((report) => (
                      <div
                        key={report.id}
                        className="rounded p-4"
                        style={{ border: '1px solid rgba(7,51,49,0.12)' }}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="space-y-0.5">
                            <div className="font-mono text-xs text-[#073331]">
                              {report.job_block_label ?? text.none}
                            </div>
                            <div className="font-mono text-[10px] text-[#607C6E]">
                              {report.inspected_at.slice(0, 10)}
                              {' · '}
                              {text.inspectorBy} {report.inspector_name ?? '—'}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <ScoreNumeral score={report.score} />
                            <PassChip passed={report.passed} label={report.passed ? text.pass : text.fail} />
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => void handleDelete(report.id)}
                                disabled={deletingId === report.id}
                                className="text-[#607C6E] transition-colors hover:text-[#C24138] disabled:opacity-50"
                              >
                                {deletingId === report.id ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Trash2 className="h-3.5 w-3.5" />
                                )}
                                <span className="sr-only">{text.delete}</span>
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="mt-2 font-mono text-xs text-[#2E4640]">
                          {report.notes ?? (
                            <span className="text-[#607C6E]">{text.noNotes}</span>
                          )}
                        </p>
                        {report.job_block_id && (
                          <div className="mt-3">
                            <QCPhotoGrid
                              jobId={report.job_id}
                              blockId={report.job_block_id}
                              token={token}
                              language={language}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              <DialogFooter className="border-t border-[#073331]/10 bg-white px-6 py-4">
                <button
                  type="button"
                  onClick={() => setOpenReportJobId(null)}
                  className="cmd-label rounded px-3 py-1.5 text-[#8A6A47] transition-colors hover:bg-[#073331]/[0.04] hover:text-[#073331]"
                  style={{ border: '1px solid rgba(7,51,49,0.12)' }}
                >
                  {text.close}
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        );
      })()}
    </div>
  );
}
